import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuthStatus } from "@/hooks/useAuth";
import { useLogements } from "@/hooks/useLogements";
import type { LogementSummary } from "@/api/logements";

export default function DashboardPage() {
  const { user } = useAuthStatus();
  const { data: logements, isLoading, error } = useLogements();

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Mes logements</h1>
            {user && (
              <p className="mt-1 text-sm text-gray-600">
                Connecté en tant que {user.email}
              </p>
            )}
          </div>
          <Link
            to="/logements/new"
            className="rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
          >
            + Nouveau logement
          </Link>
        </div>

        {isLoading && <p className="mt-8 text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-8 text-red-700">
            Impossible de récupérer la liste des logements.
          </p>
        )}

        {logements && logements.length === 0 && <EmptyState />}

        {logements && logements.length > 0 && (
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {logements.map((l) => (
              <LogementCard key={l.id} logement={l} />
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

function EmptyState() {
  return (
    <div className="mt-8 rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center">
      <p className="text-gray-600">Vous n&apos;avez pas encore d&apos;estimation.</p>
      <Link
        to="/logements/new"
        className="mt-4 inline-block rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
      >
        + Créer ma première estimation
      </Link>
    </div>
  );
}

function LogementCard({ logement }: { logement: LogementSummary }) {
  return (
    <li>
      <Link
        to={`/logements/${logement.id}`}
        className="block rounded border border-gray-200 bg-white p-4 hover:border-blue-700 hover:shadow"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold">{logement.title}</h2>
            <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">
              {logement.type}
            </p>
          </div>
          {logement.latest_dpe_label ? (
            <span className="rounded bg-blue-100 px-2 py-1 text-sm font-semibold text-blue-900">
              {logement.latest_dpe_label}
            </span>
          ) : (
            <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
              à compléter
            </span>
          )}
        </div>
        {(logement.city || logement.postal_code) && (
          <p className="mt-2 text-sm text-gray-600">
            {logement.postal_code} {logement.city}
          </p>
        )}
      </Link>
    </li>
  );
}
